import hoist from 'hoist-non-react-statics';
import {
    hot,
} from 'react-hot-loader';
import {
    connect,
} from 'react-redux';

import {
    operations,
    selectors as browserMeta,
} from 'wanderu-ducks-browser-meta';
import {
    partnerActions,
} from 'wanderu-ducks-partner-data';
import {
    splitTestSelectors,
} from 'wanderu-ducks-split-test';
import {
    i18nCoordinator,
    selectCurrency,
    selectLocale,
    setLocale,
    setLocaleAndCurrency,
    setCurrency,
    setSupportedLocales,
    setSupportedCurrencies,
} from 'wanderu-i18n';

import { App } from './App';
import localesConfig from '../../config/baseApp.locales';
import {
    selectGlobalBannerMessage,
    selectGlobalBannerVisible,
} from '../../ducks/appstate/selectors';
import {
    actions as accountActions,
    selectors as accountSelectors,
} from '../../userDuckSwap';

const LOCALE_MAP = localesConfig.localeMap;
const CURRENCY_MAP = localesConfig.currencyMap;

App.serverFetchData = async (opts) => {
    const {
        dispatch,
        i18nCoordinator: serverI18nCoordinator,
        params,
        query,
    } = opts;

    function onCurrencyChange(currency) {
        if (!currency) {
            return;
        }

        dispatch(setCurrency(currency));
    }

    function onLocaleChange(locale) {
        if (!locale) {
            return;
        }

        dispatch(setLocale(locale));
    }

    function onSupportedLocalesChange(supportedLocales) {
        dispatch(setSupportedLocales(supportedLocales));
    }

    function onSupportedCurrenciesChange(supportedCurrencies) {
        dispatch(setSupportedCurrencies(supportedCurrencies));
    }

    const { locale: urlLocale } = params;
    const { cur: urlCurrency } = query;

    const {
        defaultCurrency,
        defaultLocale,
    } = localesConfig;

    const mappedLocale = LOCALE_MAP[urlLocale];
    const localeToSet = mappedLocale || urlLocale || defaultLocale;
    const defaultCurrencyToSet = CURRENCY_MAP[localeToSet] || defaultCurrency;
    const currencyToSet = (urlCurrency || defaultCurrencyToSet).toUpperCase();

    dispatch(setLocale(localeToSet));
    dispatch(setCurrency(currencyToSet));

    serverI18nCoordinator.registerListeners({
        onCurrencyChange,
        onLocaleChange,
        onSupportedCurrenciesChange,
        onSupportedLocalesChange,
    });

    serverI18nCoordinator.init({
        currency: currencyToSet,
        defaultCurrency: defaultCurrencyToSet,
        defaultLocale,
        locale: localeToSet,
        localeToCurrencyMap: CURRENCY_MAP,
    });

    await serverI18nCoordinator.registerTranslatableApp({
        ...localesConfig,
        translationFetchFn: (locale) => {
            return import(`../../translations/baseApp/${locale}.po`);
        },
    });

    return serverI18nCoordinator.initI18nLibraries(localeToSet, currencyToSet);
};

function mapStateToProps(state, ownProps) {
    return {
        currency: selectCurrency(state),
        globalBanner: selectGlobalBannerMessage(state, ownProps),
        globalBannerVisible: selectGlobalBannerVisible(state),
        inEuropeanUnion: browserMeta.selectUserLocation(state)?.inEuropeanUnion,
        isLoggedIn: accountSelectors.selectUserLoggedIn(state),
        locale: selectLocale(state),
        splitTestAssignedEvent:
            splitTestSelectors.selectSnowplowTestAssignedEventData(state),
        userFirstName: accountSelectors.selectUserInfo(state).firstName,
    };
}

const mapDispatchToProps = {
    fetchBrowserMeta: operations.fetchBrowserMeta,
    onLogOut: accountActions.logOut,
    performAutoLogIn: accountActions.autoLogIn,
    setCurrency,
    setLocale,
    setLocaleAndCurrency,
    setPartnerDetails: partnerActions.setPartnerDetails,
    setSupportedCurrencies,
    setSupportedLocales,
};

export { App };
const connectedApp = connect(mapStateToProps, mapDispatchToProps)(App);

export default module.hot
    ? hoist(hot(module)(connectedApp), connectedApp)
    : connectedApp;

function hydrate(i18nData) {
    const {
        currency,
        currencyConversionRates,
        defaultCurrency,
        defaultLocale,
        locale,
        localeToCurrencyMap,
        supportedCurrencies,
        translations,
    } = i18nData;

    i18nCoordinator.hydrate({
        currency,
        currencyConversionRates,
        defaultCurrency,
        defaultLocale,
        locale,
        localeToCurrencyMap,
        supportedCurrencies,
        translations,
    });
}

export { hydrate };
